import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { Loader2, Filter, ExternalLink } from 'lucide-react';
import { useResolutionPipelineStore } from '@/stores/resolutionPipeline';
import { useKnowledgeStore } from '@/stores/knowledge';
import type {
  ResolutionBatchSummary,
  ResolutionResultData,
} from '@/services/api';

const DECISION_COLORS: Record<string, string> = {
  merge: 'bg-emerald-400',
  link: 'bg-blue-400',
  distinct: 'bg-slate-300',
  promote: 'bg-violet-400',
};

function DecisionBar({
  counts,
  total,
}: {
  counts: { merge: number; link: number; distinct: number; promote: number };
  total: number;
}) {
  if (total === 0) return <div className="w-32 h-1.5 rounded-full bg-slate-100" />;
  return (
    <div
      className="flex h-1.5 rounded-full overflow-hidden w-32 bg-slate-100"
      title={`merge ${counts.merge} · link ${counts.link} · distinct ${counts.distinct} · promote ${counts.promote}`}
    >
      {(['merge', 'link', 'distinct', 'promote'] as const).map((k) => {
        const pct = counts[k] > 0 ? (counts[k] / total) * 100 : 0;
        if (pct === 0) return null;
        return (
          <div
            key={k}
            className={DECISION_COLORS[k]}
            style={{ width: `${pct}%` }}
          />
        );
      })}
    </div>
  );
}

function formatTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function BatchesTab() {
  const {
    batchesFilters,
    batches,
    batchesLoading,
    selectedBatch,
    batchResults,
    batchResultsLoading,
    batchResultDecisionFilter,
    setBatchesFilters,
    fetchBatches,
    selectBatch,
    setBatchResultDecisionFilter,
  } = useResolutionPipelineStore();
  const { entityTypes, fetchEntityTypes } = useKnowledgeStore();

  useEffect(() => {
    fetchBatches();
    fetchEntityTypes();
  }, [fetchBatches, fetchEntityTypes]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
        <Filter size={12} className="text-slate-400" />
        <select
          value={batchesFilters.entity_type}
          onChange={(e) => setBatchesFilters({ entity_type: e.target.value })}
          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
        >
          <option value="">All entity types</option>
          {entityTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-xs text-slate-500">
          Limit
          <input
            type="number"
            min={5}
            max={100}
            step={5}
            value={batchesFilters.limit}
            onChange={(e) =>
              setBatchesFilters({ limit: Number(e.target.value) || 25 })
            }
            className="w-16 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-teal"
          />
        </label>
        {batchesLoading && (
          <span className="text-[10px] text-slate-400 font-mono">
            <Loader2 size={11} className="animate-spin inline mr-1" />
            loading...
          </span>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-5 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60">
            <span className="text-xs font-semibold text-navy-900">
              Batches
              <span className="ml-1 text-slate-400 font-mono">({batches.length})</span>
            </span>
          </div>
          <div className="max-h-[60vh] overflow-auto divide-y divide-slate-100">
            {batches.length === 0 && !batchesLoading ? (
              <div className="px-4 py-6 text-sm text-slate-400 text-center">
                No resolution batches yet.
              </div>
            ) : (
              batches.map((b) => (
                <BatchRow
                  key={b.batch_id}
                  batch={b}
                  selected={selectedBatch?.batch_id === b.batch_id}
                  onClick={() => selectBatch(b)}
                />
              ))
            )}
          </div>
        </div>
        <div className="col-span-7 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <BatchDetail
            batch={selectedBatch}
            results={batchResults}
            loading={batchResultsLoading}
            decisionFilter={batchResultDecisionFilter}
            onDecisionFilter={setBatchResultDecisionFilter}
          />
        </div>
      </div>
    </div>
  );
}

function BatchRow({
  batch,
  selected,
  onClick,
}: {
  batch: ResolutionBatchSummary;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-4 py-3 transition-colors',
        selected
          ? 'bg-brand-teal/5 border-l-4 border-l-brand-teal'
          : 'hover:bg-slate-50 border-l-4 border-l-transparent',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-800 truncate flex-1">
          {batch.entity_type ?? '—'}
        </span>
        <DecisionBar counts={batch.decision_counts} total={batch.total} />
      </div>
      <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
        <code className="text-[10px] truncate max-w-[10rem]">{batch.batch_id}</code>
        <span>{batch.total} results</span>
        {batch.human_reviewed > 0 && (
          <span className="text-emerald-600">
            {batch.human_reviewed} reviewed
          </span>
        )}
        {batch.overridden > 0 && (
          <span className="text-rose-600">{batch.overridden} overridden</span>
        )}
        <span className="ml-auto">{formatTime(batch.latest_at)}</span>
      </div>
    </button>
  );
}

function BatchDetail({
  batch,
  results,
  loading,
  decisionFilter,
  onDecisionFilter,
}: {
  batch: ResolutionBatchSummary | null;
  results: ResolutionResultData[];
  loading: boolean;
  decisionFilter: string;
  onDecisionFilter: (d: string) => void;
}) {
  if (!batch) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-slate-400 p-8">
        Select a batch to see individual results
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2 mb-1">
          <code className="text-[10px] text-slate-400">{batch.batch_id}</code>
          {batch.entity_type && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
              {batch.entity_type}
            </span>
          )}
          <DecisionBar counts={batch.decision_counts} total={batch.total} />
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[10px] text-slate-500">Filter</span>
          {['', 'merge', 'link', 'distinct', 'promote'].map((d) => (
            <button
              key={d || 'all'}
              onClick={() => onDecisionFilter(d)}
              className={clsx(
                'px-2 py-0.5 rounded-full text-[10px] border',
                decisionFilter === d
                  ? 'bg-brand-teal/10 border-brand-teal/30 text-brand-teal-dark'
                  : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300',
              )}
            >
              {d || 'all'}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="p-6 text-sm text-slate-400 text-center">
            <Loader2 size={12} className="animate-spin inline mr-2" />
            Loading results...
          </div>
        ) : results.length === 0 ? (
          <div className="p-6 text-sm text-slate-400 text-center">
            No results match this filter.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {results.map((r) => (
              <ResultRow key={r.id} result={r} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ResultRow({ result }: { result: ResolutionResultData }) {
  const decisionColor = DECISION_COLORS[result.decision] ?? 'bg-slate-300';
  return (
    <div className="px-5 py-3 space-y-1.5">
      <div className="flex items-center gap-2">
        <span
          className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', decisionColor)}
        />
        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border bg-white border-slate-200 text-slate-700">
          {result.decision}
        </span>
        <code className="text-[10px] text-slate-400 truncate max-w-[10rem]">
          {result.id.slice(0, 8)}
        </code>
        <span className="text-[10px] text-emerald-600 font-semibold">
          {(result.confidence * 100).toFixed(0)}%
        </span>
        {result.human_verdict && (
          <span
            className={clsx(
              'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border',
              result.human_verdict === 'confirmed'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : result.human_verdict === 'overridden'
                  ? 'bg-rose-50 text-rose-700 border-rose-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200',
            )}
          >
            {result.human_verdict}
          </span>
        )}
        {result.canonical_entity_id && (
          <Link
            to={`/knowledge/estate/entities/${result.canonical_entity_id}`}
            className="ml-auto text-[10px] text-brand-teal hover:text-brand-teal-dark inline-flex items-center gap-0.5"
          >
            canonical
            <ExternalLink size={10} />
          </Link>
        )}
      </div>
      {result.reasoning && (
        <p className="text-xs text-slate-700 whitespace-pre-wrap">
          {result.reasoning}
        </p>
      )}
      {result.key_factors && result.key_factors.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {result.key_factors.slice(0, 6).map((f, i) => (
            <span
              key={i}
              className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600"
            >
              {f}
            </span>
          ))}
        </div>
      )}
      {result.source_entity_ids && result.source_entity_ids.length > 0 && (
        <div className="text-[10px] text-slate-500 font-mono">
          sources: {result.source_entity_ids.slice(0, 5).map((s) => s.slice(0, 8)).join(', ')}
          {result.source_entity_ids.length > 5 && ' +more'}
        </div>
      )}
    </div>
  );
}
